import { createBlock } from 'factories/block';
import { InteractionProps, repeat } from './interaction';
import { createLayout } from 'factories/layout';
import { createFeedbackBlock } from './feedback';
import { colors } from 'styles/variables';
import { Block, cloneBlock, Blocks } from '@chameleoncreator/creator-app';
import { flatten } from 'factories/utils';
import { createContinueBtn } from './continueBtn';
import { createEditableBtn } from './editableBtn';
import { createMediaBlock } from './media';
import { getSId } from '../utils';

const { lGrey0 } = colors;

export type MultichoiceInteractionProps = InteractionProps & {
  answerCount: number;
};

export const createMultichoiceBlock = (count: number) => {
  return createBlock('Multichoice', [
    ...repeat(
      () =>
        createBlock('Answer', [
          createBlock('Text', [], {
            props: {
              bulletColor: '#fff',
            },
          }),
          createFeedbackBlock({}),
        ]),
      count
    ),
  ]);
};

export const createMultichoiceBlockWithText = (count: number) => {
  return createBlock('MultichoiceWithText', [
    createBlock('Text', []),
    createEditableBtn({
      type: 'Submit_Button',
      label: 'Submit',
    }),
    createMultichoiceBlock(count),
  ]);
};

const contentForColumn = (count: number) => (i: number) => {
  return [
    () =>
      createMediaBlock({
        src: undefined,
        child: 1,
        useBackgroundControls: true,
      }),
    () => createMultichoiceBlockWithText(count),
  ][i];
};

export const removeMultichoiceOption = (
  block: Block,
  position: number
): [Block, Blocks?] => {
  if (block.type === 'Multichoice') {
    const optionBlock = createBlock('Answer', [
      createBlock('Text'),
      createFeedbackBlock({}),
    ]);
    const [newBlocks] = flatten(optionBlock);

    const nextBlock = cloneBlock(block);
    (nextBlock.children as Block[]).splice(position, 1);

    return [nextBlock, newBlocks];
  }
  return [block, undefined];
};

export const addMultichoiceOption = (
  block: Block,
  position: number
): [Block, Blocks?] => {
  if (block.type === 'Multichoice') {
    const optionBlock = createBlock('Answer', [
      createBlock('Text'),
      createFeedbackBlock({}),
    ]);
    const optionBlockRef = { id: optionBlock.id, type: 'Ref' };
    const [newBlocks] = flatten(optionBlock);

    const nextBlock = cloneBlock(block);
    (nextBlock.children as Block[]).splice(position, 0, optionBlockRef);

    return [nextBlock, newBlocks];
  }
  return [block, undefined];
};

export const createMultichoiceInteractionBlock = (
  props: MultichoiceInteractionProps
) => {
  const {
    textColor = '',
    panelBackground = lGrey0,
    optionTextColor = '#fff',
    checkboxColor = '#fff',
    correctOptions = '1',
    numberOfAttempts = 1,
    lockDown = 0,
    showCorrectAnswer = 1,
    useUniqueOptionFeedback = false,
    score = true,
    style,
  } = props as any;

  return createBlock(
    'Interaction',
    [
      createLayout(
        contentForColumn(props.answerCount),
        props.columnCount,
        props.columnsReversed
      ),
      createFeedbackBlock({}),
      createBlock('SingleColumnLayout', [
        createBlock('Column', [createContinueBtn(style)], {
          props: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap',
          },
          meta: {
            subType: 'ButtonBlock',
          },
        }),
      ]),
    ],
    {
      props: {
        name: props.name,
        sId: getSId('interaction'),
        style: props.style,
        textColor,
        panelBackground,
        optionTextColor,
        checkboxColor,
        correctOptions,
        numberOfAttempts,
        lockDown,
        showCorrectAnswer,
        useUniqueOptionFeedback,
        score,
      },
      meta: {
        subType: 'Multichoice',
        layout: `${props.columnCount === 0 ? 'OneColumn' : 'TwoColumns'}${
          props.columnsReversed ? 'Reversed' : ''
        }`,
      },
    }
  );
};

export const create = createMultichoiceInteractionBlock;
export const interactionConfig = {};
